// src/features/inventory/pages/ItemDetailPage/RecordTransactionDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as OutIcon,
  ArrowUpward as InIcon,
  SwapVert as EitherIcon,
} from '@mui/icons-material';
import { stockAPI } from '../../services/stock';
import { useApi } from '../../../../hooks/api/useApi';
import { STOCK_REASONS, REASON_DIRECTION, EMPTY_TRANSACTION_FORM } from '../../constants/stockTransactions';
import { useStockLocations } from '../../hooks/useStockLocations';
import ReceiptCostFields, { parseUnitCost } from '../../components/ReceiptCostFields';
import InfoTip, { ExplainTooltip } from '../../../../components/common/InfoTip';

const REASON_ICONS = { 1: <InIcon />, '-1': <OutIcon />, 0: <EitherIcon /> };

/**
 * Unified stock transaction dialog.
 *
 * The reason comes first and decides the direction: the quantity is always
 * typed as a positive number, so nobody can "dispense" stock onto the shelf.
 * Only an adjustment asks which way it goes.
 *
 * Usage A — item-level (no lot):
 *   <RecordTransactionDialog itemId={id} currentStock={n} uom="stk" lotTracked />
 *   When lotTracked=true only removals are offered (FIFO deduction via backend);
 *   stock is added through a new batch instead.
 *
 * Usage B — lot-level:
 *   <RecordTransactionDialog itemId={id} lot={lotObject} />
 *   The lot's quantity_on_hand is used as current stock and lot_id is included in the request.
 */
export default function RecordTransactionDialog({
  open, onClose, onSuccess,
  itemId,
  currentStock,
  uom = '',
  lotTracked = false,
  lot = null,          // when provided: lot-level adjustment
}) {
  const { t } = useTranslation(['inventory', 'common']);
  const { loading, error, execute, clearError } = useApi();
  const [form, setForm] = useState({ ...EMPTY_TRANSACTION_FORM, location_id: '' });
  const [adjustDirection, setAdjustDirection] = useState(null);
  const [unitCost, setUnitCost] = useState('');
  const [updatePrice, setUpdatePrice] = useState(true);
  const [balances, setBalances] = useState([]);
  const { stockLocations, fetchStockLocations } = useStockLocations();
  const { execute: executeBalances } = useApi();

  // When a lot is passed in, derive stock from it; otherwise use the explicit prop.
  const stock = lot != null ? lot.quantity_on_hand : (currentStock ?? 0);
  // A lot-tracked item without a lot can only lose stock here (FIFO); adding goes through a new batch.
  const fifoMode = lotTracked && lot == null;
  const reasons = fifoMode ? STOCK_REASONS.filter((r) => REASON_DIRECTION[r] <= 0) : STOCK_REASONS;

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_TRANSACTION_FORM, location_id: '' });
      setAdjustDirection(null);
      setUnitCost('');
      setUpdatePrice(true);
      fetchStockLocations();
    }
  }, [open, fetchStockLocations]);

  // Where the stock sits: a lot carries its own split, the item's is fetched
  useEffect(() => {
    if (!open) return undefined;
    if (lot) { setBalances(lot.location_balances ?? []); return undefined; }
    setBalances([]);
    let cancelled = false;
    executeBalances(() => stockAPI.getStockLevelByLocation(itemId), { showError: false }).then((result) => {
      if (!cancelled && result.success) setBalances(result.data.data?.location_balances ?? []);
    });
    return () => { cancelled = true; };
  }, [open, lot, itemId, executeBalances]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  // Digits only: the reason gives the sign
  const setQuantity = (e) => setForm(prev => ({ ...prev, quantity: e.target.value.replace(/\D/g, '') }));

  const quantity = form.quantity === '' ? 0 : parseInt(form.quantity, 10);
  const isAdjustment = form.reason === 'adjustment';
  const direction = !form.reason ? null
    : !isAdjustment ? REASON_DIRECTION[form.reason]
      : fifoMode ? -1 : adjustDirection;
  const hasValue = direction != null && quantity > 0;
  const change = hasValue ? direction * quantity : 0;
  const quantityAfter = stock + change;
  const outbound = direction === -1;

  // Location works like the scanner: stock is taken from a place that has it
  // (most first), and received where the item already lives. Taking stock out
  // "from nowhere" would leave the place it really came from overstated.
  const activeLocations = stockLocations.filter(l => l.active);
  const locationName = (b) => (b.location_id == null ? t('inventory:items.no_location') : b.location_name);
  const stockedBuckets = balances.filter((b) => b.quantity > 0).sort((a, b) => b.quantity - a.quantity);
  const showLocation = outbound
    ? stockedBuckets.some((b) => b.location_id != null)
    : activeLocations.length > 0;
  const homeLocation = stockedBuckets.find((b) => (
    b.location_id != null && activeLocations.some((l) => l.stock_location_id === b.location_id)
  ))?.location_id;
  const defaultLocation = outbound
    ? (stockedBuckets[0]?.location_id ?? '')
    : (homeLocation ?? (activeLocations.length === 1 ? activeLocations[0].stock_location_id : ''));

  useEffect(() => {
    setForm(prev => ({ ...prev, location_id: defaultLocation }));
  }, [open, defaultLocation]);

  const bucket = outbound && showLocation
    ? stockedBuckets.find((b) => (b.location_id ?? '') === form.location_id)
    : null;
  const available = bucket ? bucket.quantity : stock;
  const notEnough = hasValue && (quantityAfter < 0 || (outbound && quantity > available));
  // A receipt can carry its purchase cost
  const isReceipt = form.reason === 'received';
  const costValid = !isReceipt || unitCost === '' || parseUnitCost(unitCost) != null;
  const isValid = hasValue && !notEnough && costValid;

  const handleClose = () => { clearError(); onClose(); };

  const handleSubmit = async () => {
    if (!isValid) return;
    const result = await execute(() =>
      stockAPI.recordTransaction({
        item_id: itemId,
        lot_id: lot ? lot.lot_id : null,
        quantity_change: change,
        reason: form.reason,
        notes: form.notes.trim() || null,
        location_id: form.location_id || null,
        unit_cost: isReceipt ? parseUnitCost(unitCost) : null,
        update_cost_price: updatePrice,
      })
    );
    if (result.success) { onSuccess(); handleClose(); }
  };

  const changeSign = change > 0 ? '+' : '';
  const changeColor = change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.secondary';

  let quantityHelper = null;
  if (notEnough && bucket) quantityHelper = t('inventory:items.stock_status.not_enough_at', { count: available, uom, location: locationName(bucket) });
  else if (notEnough) quantityHelper = t('inventory:items.stock_status.not_enough', { count: stock, uom });
  else if (fifoMode) quantityHelper = null;
  else if (isAdjustment && adjustDirection == null) quantityHelper = t('inventory:items.stock_status.choose_direction');
  else if (direction === 1) quantityHelper = t('inventory:items.stock_status.adds_stock');
  else if (direction === -1) quantityHelper = t('inventory:items.stock_status.removes_stock');

  const title = fifoMode
    ? t('inventory:items.stock_status.dialog_title_fifo')
    : t('inventory:items.stock_status.dialog_title');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {title}
        {lot && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: -2 }}>
            {t('inventory:items.tabs.batches.columns.lot_number')}: <strong>{lot.lot_number}</strong>
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {error && <Alert severity="error" onClose={clearError}>{error}</Alert>}

          {/* Live preview: current, change, new balance — read left to right */}
          <Box sx={{
            display: 'flex',
            mx: -6,
            mt: -1,
            mb: 2,
            px: 6,
            py: 2,
            bgcolor: 'background.default',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="tiny" color="text.secondary" noWrap>
                  {t('inventory:items.tabs.batches.adjust_stock.current_qty')}
                </Typography>
                <InfoTip size={13} title={t('inventory:help.stock.preview_current')} />
              </Box>
              <Typography variant="h4" lineHeight={1.1}>
                {stock}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="tiny" color="text.secondary" noWrap>
                  {t('inventory:items.tabs.batches.adjust_stock.change')}
                </Typography>
                <InfoTip size={13} title={t('inventory:help.stock.preview_change')} />
              </Box>
              <Typography
                variant="h4"
                lineHeight={1.1}
                color={hasValue ? changeColor : 'text.disabled'}
              >
                {hasValue ? `${changeSign}${change}` : '0'}
              </Typography>
            </Box>

            <Box sx={{ width: '1px', bgcolor: 'divider', mx: 3, alignSelf: 'stretch' }} />

            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="tiny" color="text.secondary" noWrap>
                  {t('inventory:items.stock_status.new_balance')}
                </Typography>
                <InfoTip size={13} title={t('inventory:help.stock.preview_new')} />
              </Box>
              <Typography
                variant="h4"
                lineHeight={1.1}
                color={!hasValue ? 'text.disabled' : quantityAfter < 0 ? 'error.main' : 'info.main'}
              >
                {quantityAfter}
              </Typography>
            </Box>
          </Box>

          {/* Reason first: it decides whether stock goes up or down */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
              {t('inventory:items.tabs.batches.adjust_stock.reason')} *
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {reasons.map((r) => (
                <ExplainTooltip key={r} title={t(`inventory:help.reasons.${r}`)} enterDelay={400}>
                  <Chip
                    icon={REASON_ICONS[r === 'adjustment' && fifoMode ? -1 : REASON_DIRECTION[r]]}
                    label={t(`inventory:items.stock_reasons.${r}`)}
                    onClick={() => setForm(prev => ({ ...prev, reason: r }))}
                    variant={form.reason === r ? 'filled' : 'outlined'}
                    color="info"
                    disabled={loading}
                    clickable
                    size="small"
                  />
                </ExplainTooltip>
              ))}
            </Box>
          </Box>

          {isAdjustment && !fifoMode && (
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              color="info"
              value={adjustDirection}
              onChange={(_, value) => value != null && setAdjustDirection(value)}
              aria-label={t('inventory:items.stock_status.adjust_direction')}
              disabled={loading}
            >
              <ToggleButton value={1}>
                <InIcon fontSize="small" sx={{ mr: 1 }} />
                {t('inventory:items.stock_status.add')}
              </ToggleButton>
              <ToggleButton value={-1}>
                <OutIcon fontSize="small" sx={{ mr: 1 }} />
                {t('inventory:items.stock_status.remove')}
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Quantity + location side by side */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label={t('inventory:items.stock_status.quantity')}
              value={form.quantity}
              onChange={setQuantity}
              required
              disabled={loading}
              error={notEnough}
              helperText={quantityHelper}
              slotProps={{
                htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
                input: uom ? { endAdornment: <InputAdornment position="end">{uom}</InputAdornment> } : undefined,
              }}
              sx={{ flex: 1 }}
            />

            {showLocation && (
              <TextField
                select
                label={t('inventory:items.tabs.batches.adjust_stock.location')}
                value={form.location_id}
                onChange={set('location_id')}
                disabled={loading}
                // The field shows the place; what is there goes underneath, so neither gets cut off
                helperText={bucket ? t('inventory:items.transfer_stock.available_hint', { qty: bucket.quantity, uom }) : null}
                slotProps={outbound ? {
                  select: {
                    renderValue: (value) => locationName(stockedBuckets.find((b) => (b.location_id ?? '') === value) ?? { location_id: null }),
                  },
                } : undefined}
                sx={{ flex: 1 }}
              >
                {outbound
                  ? stockedBuckets.map((b) => (
                    <MenuItem key={b.location_id ?? 'none'} value={b.location_id ?? ''}>
                      {locationName(b)} · {b.quantity} {uom}
                    </MenuItem>
                  ))
                  : [
                    <MenuItem key="none" value="">
                      <Typography color="text.secondary" variant="body2">
                        {t('inventory:items.tabs.batches.dialog.no_location')}
                      </Typography>
                    </MenuItem>,
                    ...activeLocations.map((loc) => (
                      <MenuItem key={loc.stock_location_id} value={loc.stock_location_id}>
                        {loc.name}
                      </MenuItem>
                    )),
                  ]}
              </TextField>
            )}
          </Box>

          {fifoMode && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              {t('inventory:items.tabs.batches.adjust_stock.quantity_hint_fifo')}
            </Typography>
          )}

          {isReceipt && (
            <ReceiptCostFields
              open={open}
              itemId={itemId}
              uom={uom}
              value={unitCost}
              onChange={setUnitCost}
              updatePrice={updatePrice}
              onUpdatePriceChange={setUpdatePrice}
              disabled={loading}
            />
          )}

          <TextField
            label={t('inventory:items.tabs.batches.adjust_stock.notes')}
            value={form.notes}
            onChange={set('notes')}
            multiline
            rows={2}
            disabled={loading}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? t('common:buttons.saving') : t('inventory:items.tabs.batches.adjust_stock.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
